import React from 'react';
import { 
    Box, Typography, Container, Grid, Card, CardContent, 
    Paper, useTheme, Chip, Divider, Alert, Button, 
} from '@mui/material';
import { 
    History as HistoryIcon, 
    NewReleases as NewReleasesIcon, 
    BugReport as BugReportIcon,
    AutoFixHigh as AutoFixHighIcon,
    Upgrade as UpgradeIcon,
    OpenInNew as OpenInNewIcon,
    CalendarMonth as CalendarMonthIcon
} from '@mui/icons-material';
// Ensure @mui/lab is installed: npm install @mui/lab
import { 
    Timeline, 
    TimelineItem, 
    TimelineSeparator, 
    TimelineConnector, 
    TimelineContent, 
    TimelineDot 
} from '@mui/lab';

// --- MOCK DATA ---
const releases = [
    {
        version: 'v2.1.0',
        date: '2025-10-06',
        isCurrent: true,
        label: 'Security, Compliance & System Monitoring Suite',
        color: 'primary',
        notes: [
            { type: 'Feature', description: 'Implemented **Data Privacy & Compliance** Module (DSAR, GDPR tools).', icon: NewReleasesIcon },
            { type: 'Feature', description: 'Deployed new **System Health Monitoring** dashboard with real-time metrics.', icon: NewReleasesIcon },
            { type: 'Fix', description: 'Resolved critical memory leak issue in background job worker.', icon: BugReportIcon },
            { type: 'Improvement', description: 'Optimized database query performance for User Management API.', icon: AutoFixHighIcon },
        ]
    },
    {
        version: 'v2.0.1',
        date: '2025-09-15',
        isCurrent: false,
        label: 'Hotfix & Security Patch',
        color: 'warning',
        notes: [
            { type: 'Fix', description: 'Patched XSS vulnerability in the public login endpoint.', icon: BugReportIcon },
            { type: 'Improvement', description: 'Increased session timeout limit for admin users.', icon: AutoFixHighIcon },
        ]
    },
    {
        version: 'v2.0.0',
        date: '2025-09-01',
        isCurrent: false,
        label: 'Major Platform Relaunch & Infrastructure Migration',
        color: 'secondary',
        notes: [
            { type: 'Feature', description: 'Full migration to new cloud infrastructure (AWS -> GCP).', icon: UpgradeIcon },
            { type: 'Feature', description: 'Implemented Custom Branding and Access Control settings pages.', icon: NewReleasesIcon },
            { type: 'Improvement', description: 'Upgraded React framework to v18.', icon: AutoFixHighIcon },
        ]
    },
];

// Helper to get appropriate chip color based on type
const getNoteColor = (type) => {
    switch (type) {
        case 'Feature': return 'primary';
        case 'Fix': return 'error';
        case 'Improvement': return 'success';
        default: return 'default';
    }
};

// --- MAIN COMPONENT ---
const ReleaseFeatureLogs = () => {
    const theme = useTheme();
    const currentRelease = releases.find(r => r.isCurrent) || releases[0];

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Typography 
                variant="h4" 
                component="h1" 
                gutterBottom 
                sx={{ 
                    fontWeight: 800, 
                    color: theme.palette.primary.main, 
                    display: 'flex',
                    alignItems: 'center',
                    pb: 1
                }}
            >
                <HistoryIcon sx={{ mr: 1.5, fontSize: '3rem', color: theme.palette.secondary.main }} />
                System Release & Change Log
            </Typography>
            <Typography variant="h6" color="textSecondary" sx={{ mb: 4 }}>
                A chronological timeline of feature deployments, bug fixes, and system infrastructure improvements.
            </Typography>

            <Paper elevation={6} sx={{ p: { xs: 2, md: 4 }, borderRadius: 2 }}>
                
                <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} mb={3}>
                    <Alert 
                        severity="info" 
                        icon={<CalendarMonthIcon fontSize="inherit" />}
                        sx={{ flexGrow: 1, mr: { sm: 2 }, mb: { xs: 2, sm: 0 }, width: { xs: '100%', sm: 'auto' } }}
                    >
                        Current Version: **{currentRelease.version}** | Deployed: {currentRelease.date}
                    </Alert>
                    <Button variant="contained" color="secondary" startIcon={<OpenInNewIcon />}>
                        View Full Change Log Documentation
                    </Button>
                </Box>
                
                <Divider sx={{ mb: 4 }} />
                
                <Typography variant="h5" fontWeight={700} mb={3}>
                    Deployment Timeline
                </Typography>

                {/* Timeline with alternate positioning for better presentation on large screens */}
                <Timeline position="alternate" sx={{ padding: 0 }}>
                    {releases.map((release, index) => (
                        <TimelineItem key={index}>
                            
                            {/* --- Separator and Dot --- */}
                            <TimelineSeparator>
                                <TimelineDot 
                                    color={release.color} 
                                    variant={release.isCurrent ? "filled" : "outlined"}
                                    sx={{ p: 1 }}
                                >
                                    <UpgradeIcon />
                                </TimelineDot>
                                {/* Only show connector between items, not after the last one */}
                                {index < releases.length - 1 && <TimelineConnector sx={{ minHeight: 60 }} />}
                            </TimelineSeparator>
                            
                            {/* --- Content Card --- */}
                            <TimelineContent sx={{ py: '12px', px: 2 }}>
                                <Card elevation={6} sx={{ borderRadius: 2, border: release.isCurrent ? `2px solid ${theme.palette.primary.light}` : 'none' }}>
                                    <CardContent>
                                        
                                        {/* Version and Date Header */}
                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                            <Typography variant="h6" fontWeight={800} color={theme.palette[release.color].main}>
                                                {release.version}
                                            </Typography>
                                            <Chip 
                                                label={release.date} 
                                                size="small" 
                                                variant="outlined" 
                                                color="default"
                                                sx={{ fontWeight: 500 }}
                                            />
                                        </Box>
                                        <Typography variant="subtitle2" color="textPrimary" fontWeight={600} mb={2}>
                                            {release.label}
                                        </Typography>
                                        
                                        <Divider sx={{ mb: 1.5 }} />

                                        {/* Release Notes Grid */}
                                        <Grid container spacing={1}>
                                            {release.notes.map((note, noteIndex) => {
                                                const NoteIcon = note.icon;
                                                return (
                                                    <Grid item xs={12} key={noteIndex} sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                                        <Chip 
                                                            label={note.type}
                                                            color={getNoteColor(note.type)}
                                                            size="small"
                                                            icon={<NoteIcon sx={{ fontSize: 16 }} />}
                                                            sx={{ mr: 1.5, minWidth: '100px', fontWeight: 600, height: 28 }}
                                                        />
                                                        <Typography variant="body2" flexGrow={1} sx={{ mt: 0.2, color: theme.palette.text.secondary }}>
                                                            {note.description}
                                                        </Typography>
                                                    </Grid>
                                                );
                                            })}
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </TimelineContent>
                        </TimelineItem>
                    ))}
                </Timeline>
            </Paper>
        </Container>
    );
};

export default ReleaseFeatureLogs;